import "./App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "./components/ui/toaster";
import useReveal from "./hooks/useReveal";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Segments from "./components/Segments";
import Stats from "./components/Stats";
import Services from "./components/Services";
import WorkModel from "./components/WorkModel";
import Process from "./components/Process";
import PriceCalculator from "./components/PriceCalculator";
import RoofColorVisualizer from "./components/RoofColorVisualizer";
import BeforeAfter from "./components/BeforeAfter";
import Testimonials from "./components/Testimonials";
import GoogleReviews from "./components/GoogleReviews";
import Coverage from "./components/Coverage";
import FAQ from "./components/FAQ";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import FloatingCTA from "./components/FloatingCTA";
import Positioning from "./components/Positioning";
import TakKoll from "./components/TakKoll";
import Takrapport from "./components/Takrapport";
import HprCare from "./components/HprCare";
import B2B from "./components/B2B";
import { initAnalytics } from "./lib/analytics";
import { stockholmSlugs } from "./stockholmData";
import { locations } from "./locationData";
import { posts } from "./blogData";

const TakmalningPage = lazy(() => import("./components/TakmalningPage"));
const SolarPage = lazy(() => import("./components/SolarPage"));
const GalleryPage = lazy(() => import("./components/GalleryPage"));
const AdminPage = lazy(() => import("./components/AdminPage"));
const QuotePage = lazy(() => import("./components/QuotePage"));
const LocationPage = lazy(() => import("./components/LocationPage"));
const BlogIndex = lazy(() => import("./components/BlogIndex"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const PaymentSuccessPage = lazy(() => import("./components/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./components/PaymentCancelPage"));
const OnengoLogin = lazy(() => import("./components/onengo/OnengoLogin"));
const OnengoDashboard = lazy(() => import("./components/onengo/OnengoDashboard"));
const OnengoSign = lazy(() => import("./components/onengo/OnengoSign"));
const CustomerPortal = lazy(() => import("./components/onengo/CustomerPortal"));
const MeetingRoom = lazy(() => import("./components/onengo/MeetingRoom"));
const InvestorView = lazy(() => import("./components/onengo/InvestorView"));
const CampaignLanding = lazy(() => import("./components/CampaignLanding"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const ServiceLanding = lazy(() => import("./components/ServiceLanding"));
const B2BLanding = lazy(() => import("./components/B2BLanding"));
const TakKollLanding = lazy(() => import("./components/TakKollLanding"));
const BrfLanding = lazy(() => import("./components/BrfLanding"));
const AboutHpr = lazy(() => import("./components/AboutHpr"));
const ContactPage = lazy(() => import("./components/ContactPage"));

function Home() {
  useReveal();
  return (
    <div className="App">
      <Helmet>
        <title>Taktvätt & Takmålning Stockholm | HPR – High Performance Roofing</title>
        <meta name="description" content="Professionell taktvätt, takmålning och takbehandling i Stockholm och Uppsala. Få en kostnadsfri takbesiktning och offert från HPR." />
        <link rel="canonical" href="https://hproofing.se/" />
      </Helmet>
      <Header />
      <main>
        <Hero />
        <Positioning />
        <Segments />
        <Stats />
        <Services />
        <BeforeAfter />
        <TakKoll />
        <RoofColorVisualizer />
        <WorkModel />
        <Process />
        <PriceCalculator />
        <Takrapport />
        <HprCare />
        <B2B />
        <Testimonials />
        <GoogleReviews />
        <Coverage />
        <FAQ />
        <Contact />
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}

function RootRoute() {
  const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  if (host.includes("onego")) {
    if (typeof window !== "undefined") window.location.replace("/onengo.html");
    return null;
  }
  return <Home />;
}

function SeoMetaCleanup() {
  const location = useLocation();
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const descriptions = Array.from(document.querySelectorAll('meta[name="description"]'));
      descriptions.slice(0, -1).forEach((tag) => tag.remove());
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);
  return null;
}

function RouteIndexingPolicy() {
  const { pathname } = useLocation();
  const privatePath = ["/admin", "/onengo", "/offert/", "/signera/", "/portal/", "/mote/", "/investerare/", "/offert-preview/", "/betalning/"].some((prefix) => pathname === prefix || pathname.startsWith(prefix));
  return privatePath ? <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet> : null;
}

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SeoMetaCleanup />
        <RouteIndexingPolicy />
        <Suspense fallback={<div className="min-h-screen hpr-gradient" data-testid="route-loading" aria-label="Laddar sida" />}>
          <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/kampanj/taktvatt" element={<CampaignLanding variant="taktvatt" />} />
          <Route path="/kampanj/solcellstvatt" element={<CampaignLanding variant="solcellstvatt" />} />
          <Route path="/kampanj/takkoll" element={<CampaignLanding variant="takkoll" />} />
          <Route path="/kampanj/takmalning" element={<CampaignLanding variant="takmalning" />} />
          <Route path="/tack" element={<ThankYou />} />
          <Route path="/foretag" element={<B2BLanding />} />
          <Route path="/brf" element={<Navigate to="/brf-takvard" replace />} />
          <Route path="/brf-takvard" element={<BrfLanding />} />
          <Route path="/om-hpr" element={<Navigate to="/om-oss" replace />} />
          <Route path="/om-oss" element={<AboutHpr />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/boka-takkoll" element={<TakKollLanding />} />
          <Route path="/solcellstvatt" element={<SolarPage />} />
          <Route path="/galleri" element={<Navigate to="/referenser" replace />} />
          <Route path="/referenser" element={<GalleryPage />} />
          <Route path="/takmalning" element={<TakmalningPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/offert/:id" element={<QuotePage />} />
          <Route path="/guider" element={<BlogIndex />} />
          <Route path="/betalning/klar" element={<PaymentSuccessPage />} />
          <Route path="/betalning/avbruten" element={<PaymentCancelPage />} />
          <Route path="/onengo" element={<OnengoLogin />} />
          <Route path="/onengo/dashboard" element={<OnengoDashboard />} />
          <Route path="/signera/:token" element={<OnengoSign />} />
          <Route path="/offert-preview/hpr" element={<OnengoSign preview />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/mote/:token" element={<MeetingRoom />} />
          <Route path="/investerare/:token" element={<InvestorView />} />
          {posts.map((p) => (
            <Route key={p.slug} path={`/guider/${p.slug}`} element={<BlogPost slug={p.slug} />} />
          ))}
          {stockholmSlugs.map((s) => (
            <Route key={s} path={`/${s}`} element={<ServiceLanding slug={s} />} />
          ))}
          {locations.map((l) => (
            <Route key={l.slug} path={`/${l.slug}`} element={<LocationPage slug={l.slug} />} />
          ))}
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
